import React, { useCallback, useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { toast } from 'sonner';
import { bizWorkspaceAPI as bizAPI } from '../services/bizApi';
import { pledgeAPI } from '../services/api';
import { BizShell, useBizStyles } from './BizSetupPage';
import { HandHeart } from '@phosphor-icons/react';

const BEBAS = { fontFamily: "'Bebas Neue', sans-serif" };

export default function BizPledgesPage() {
  const bs = useBizStyles();
  const [pages, setPages] = useState([]);
  const [err, setErr] = useState(null);
  const [form, setForm] = useState({ title: '', perk_description: '', price_type: 'fixed', amount: '', min_amount: '5' });
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try { setPages((await bizAPI.listPledges()).data.pages); setErr(null); }
    catch (e) { setErr(e?.response?.data?.detail || 'Failed to load'); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const submit = async (e) => {
    e.preventDefault(); setBusy(true);
    try {
      const { data } = await bizAPI.createPledge({
        title: form.title, perk_description: form.perk_description, price_type: form.price_type,
        amount: form.price_type === 'fixed' ? parseFloat(form.amount) : null,
        min_amount: parseFloat(form.min_amount || '1'),
      });
      navigator.clipboard?.writeText(`${window.location.origin}/pledge/${data.page_id}`);
      toast.success('Support page created — share link copied');
      setForm({ title: '', perk_description: '', price_type: 'fixed', amount: '', min_amount: '5' }); load();
    } catch (e2) { toast.error(e2?.response?.data?.detail || 'Failed'); }
    finally { setBusy(false); }
  };

  const inputClass = 'w-full rounded-xl px-3 h-11 text-sm focus:outline-none transition-colors';

  return (
    <BizShell title="Support Pages" subtitle="Let backers and clients fund your next project — tied to a real perk, paid direct via Stripe." testId="biz-pledges-page">
      {err && <div className="p-4 text-sm text-[#F59E0B] mb-5" style={bs.cardStyle} data-testid="pledges-error">{err}</div>}
      <form onSubmit={submit} className="p-5 mb-6 space-y-3" style={bs.cardStyle} data-testid="pledge-form">
        <input required placeholder="Title (e.g. Back our next EP)" value={form.title} onChange={(e) => setForm((f) => ({ ...f, title: e.target.value }))} className={inputClass} style={bs.inputStyle} data-testid="pledge-title" />
        <textarea required placeholder="What supporters get (the perk)" value={form.perk_description} onChange={(e) => setForm((f) => ({ ...f, perk_description: e.target.value }))} className={`${inputClass} h-20 py-2`} style={bs.inputStyle} data-testid="pledge-perk" />
        <div className="flex flex-col sm:flex-row gap-3">
          <select value={form.price_type} onChange={(e) => setForm((f) => ({ ...f, price_type: e.target.value }))} className={inputClass} style={bs.inputStyle} data-testid="pledge-type">
            <option value="fixed">Fixed price</option>
            <option value="pwyw">Pay what you want</option>
          </select>
          {form.price_type === 'fixed'
            ? <input type="number" step="0.01" min="1" required placeholder="Price (USD)" value={form.amount} onChange={(e) => setForm((f) => ({ ...f, amount: e.target.value }))} className={inputClass} style={bs.inputStyle} data-testid="pledge-amount" />
            : <input type="number" step="0.01" min="0.5" placeholder="Minimum (USD)" value={form.min_amount} onChange={(e) => setForm((f) => ({ ...f, min_amount: e.target.value }))} className={inputClass} style={bs.inputStyle} data-testid="pledge-min" />}
        </div>
        <button type="submit" disabled={busy} className="h-11 px-6 rounded-xl font-bold text-black text-sm disabled:opacity-60 hover:opacity-90 transition-opacity" style={{ background: bs.GREEN }} data-testid="pledge-submit">Create page</button>
      </form>
      <div style={bs.cardStyle} data-testid="pledges-list">
        <p className="text-[11px] tracking-[0.14em] uppercase font-semibold px-5 pt-5 pb-3" style={{ color: bs.labelColor }}>Your pages</p>
        {pages.length === 0 ? (
          <p className="text-xs px-5 pb-5" style={{ color: bs.textFaint }}>None yet.</p>
        ) : pages.map((p) => (
          <div key={p.page_id} className="px-5 py-3.5 flex items-center gap-3 text-sm" style={{ borderTop: `1px solid ${bs.listItemBorder}` }}>
            <div className="flex-1 min-w-0">
              <p className="truncate" style={{ color: bs.textPrimary }}>{p.title}</p>
              <p className="text-[10px] mt-0.5" style={{ color: bs.textMuted }}>{p.price_type === 'fixed' ? `$${p.amount}` : `pay what you want (min $${p.min_amount})`} · {p.pledge_count} pledge{p.pledge_count === 1 ? '' : 's'}</p>
            </div>
            <button
              onClick={() => { navigator.clipboard?.writeText(`${window.location.origin}/pledge/${p.page_id}`); toast.success('Link copied'); }}
              className="text-xs underline hover:opacity-80"
              style={{ color: bs.textMuted }}
              data-testid={`pledge-copy-${p.page_id}`}
            >
              copy link
            </button>
          </div>
        ))}
      </div>
    </BizShell>
  );
}

/* ---------- Public supporter-facing page ---------- */
export function PublicPledgePage() {
  const { pageId } = useParams();
  const bs = useBizStyles();
  const [page, setPage] = useState(null);
  const [amount, setAmount] = useState('');
  const [busy, setBusy] = useState(false);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    pledgeAPI.getPage(pageId).then(({ data }) => setPage(data)).catch(() => setNotFound(true));
  }, [pageId]);

  const pay = async () => {
    setBusy(true);
    try {
      const { data } = await pledgeAPI.checkout(pageId, page.price_type === 'pwyw' ? parseFloat(amount) : null, window.location.origin);
      window.location.href = data.checkout_url;
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Checkout unavailable');
      setBusy(false);
    }
  };

  const wrapperStyle = { fontFamily: "'Barlow', sans-serif", background: bs.t.pageBg, color: bs.t.textPrimary };

  if (notFound) return <div className="min-h-screen flex items-center justify-center" style={wrapperStyle}>Page not found.</div>;
  if (!page) return <div className="min-h-screen flex items-center justify-center" style={wrapperStyle}>Loading…</div>;

  const inputClass = 'w-full rounded-xl px-3 h-11 text-sm focus:outline-none transition-colors';

  return (
    <div className="min-h-screen flex items-center justify-center px-5" style={wrapperStyle} data-testid="public-pledge-page">
      <div className="p-8 w-full max-w-md text-center" style={bs.cardStyle}>
        <img src="/brand/divispend-logo.png" alt="Divispend" className="h-10 w-auto mx-auto mb-6" />
        <HandHeart className="w-10 h-10 mx-auto mb-3" style={{ color: bs.GREEN }} weight="bold" />
        <p className="text-xs mb-1" style={{ color: bs.textMuted }}>{page.business_name}</p>
        <h1 className="text-3xl mb-3" style={{ ...BEBAS, color: bs.textPrimary }} data-testid="pledge-page-title">{page.title}</h1>
        <p className="text-sm leading-relaxed mb-6" style={{ color: bs.textMuted }}>{page.perk_description}</p>
        {page.price_type === 'pwyw' && (
          <input type="number" step="0.01" min={page.min_amount} placeholder={`Amount (min $${page.min_amount})`} value={amount}
            onChange={(e) => setAmount(e.target.value)} className={`${inputClass} mb-3 text-center`} style={bs.inputStyle} data-testid="pledge-pwyw-amount" />
        )}
        <button onClick={pay} disabled={busy || (page.price_type === 'pwyw' && !amount)}
          className="w-full h-12 rounded-xl font-bold text-black text-sm disabled:opacity-50 hover:opacity-90 transition-opacity" style={{ background: bs.GREEN }} data-testid="pledge-pay-button">
          {busy ? 'Redirecting…' : page.price_type === 'fixed' ? `Support — $${page.amount}` : 'Support this project'}
        </button>
        <p className="text-[10px] mt-4" style={{ color: bs.textFaint }}>Payment processed securely by Stripe. {page.pledge_count > 0 && `${page.pledge_count} supporter${page.pledge_count === 1 ? '' : 's'} so far.`}</p>
      </div>
    </div>
  );
}
